<template>
    <div class="wrapper">
        <PortalTarget class="drawer-portal" name="drawer" />

        <!-- MENU HEADER -->
        <menu-header    
            :hideMenu="view !== 'play-game'" 
            :sideMenuOpened="showSideMenu"
            :showBackArrow="showBackArrow"
            :users="usersInMenu" 
            :view="view" 
            @handleBack="handleGoBack" 
            @toggleSideMenu="showSideMenu = !showSideMenu"
        ></menu-header>

        <!-- SIDE MENU -->
        <side-menu 
            ref="sideMenu"
            :view="view" 
            :user="user"
            @deleteGame="handleDeleteGame"
            @updateView="handleUpdateView" 
        ></side-menu>

        <!-- GAME COMPONENT -->
        <play-game 
            v-if="showGameComponent" 
            class="game-wrapper" 
            :socket="socket" 
            :users="users" 
            :games="games" 
            :view="view"
            @closeSideMenu="handleCloseSideMenu"
            @register="updateYou" 
            @updateView="handleUpdateView"
            @gameCreated="handleGameCreated"
        ></play-game>

        <settings
            v-else-if="view === 'settings'"
                :user="user"
        ></settings>

        <!-- LOADER -->
        <div v-else class="center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="150px" height="150px">
                <circle transform-origin="center" fill="none" stroke="#505050" stroke-width="8" stroke-linecap="round" stroke-dasharray="200 1000" stroke-dashoffset="0" cx="100" cy="100" r="70">
                    <animateTransform 
                        type="rotate" 
                        attributeName="transform" 
                        calcMode="spline" 
                        dur="1" 
                        values="0;360"
                        keyTimes="0; 1"
                        keySplines="0 0 1 1"
                        repeatCount="indefinite"
                    ></animateTransform>
                    <animate
                        class="animation-tag"
                        attributeName="stroke-dashoffset"
                        from="10"
                        to="-400"
                        dur="2"
                        calcMode="spline"
                        keyTimes="0; 1"
                        keySplines="0 0 1 1"
                        repeatCount="indefinite"
                    />
                    <animate
                        class="animation-tag"
                        attributeName="stroke-dasharray"
                        from="60"
                        to="600"
                        dur="2"
                        calcMode="spline"
                        keyTimes="0; 1"
                        keySplines="0 0 1 1"
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
        </div>
    </div>
</template>
<script>
const { v4: uuidv4 } = require('uuid');
import { io } from "socket.io-client";
import { httpRequest, myfunction } from '@/utils.js';

import Dashboard from './Dashboard.vue';
import PlayGame from './PlayGame.vue';
import Settings from './Settings.vue';
import MenuHeader from './MenuHeader.vue';
import PopupModal from '@/components/PopupModal.vue';
import CustomButton from '@/components/CustomButton.vue';
import SideMenu from './SideMenu.vue';

export default {
    name: 'Application',
    components: {
        Dashboard,
        PlayGame,
        Settings,
        MenuHeader,
        PopupModal,
        CustomButton,
        SideMenu
    },
    data() {
        return {
            users: [],
            games: [],
            loading: true,
            user: null,
            view: 'select-game',
            showSideMenu: false,
            playingGame: null,
            requestNotificationPermission: false,
            unableToGrantPermission: false,
            notificationsDenied: false,
            timesRequested: 0,
        }
    },
    methods: {
        createConnection() {
            this.socket = io(process.env.VUE_APP_API_URL, {
                auth: {
                    token: process.env.VUE_APP_API_KEY
                },
                path: '/socket.io' 
            } );

            this.socket.on('connect', () => {
                console.log('socket id: ', this.socket.id)
                localStorage.setItem('socketId', this.socket.id);
            });

            this.socket.on('game_created', (game) => {
                const games = [ ...this.games, game ];
                this.games = this.parseGames(games);

                if (this.games.length > 0 && this.view === 'create-game') this.handleUpdateView('select-game');
            });

            this.socket.on('game_updated', (game) => {
                console.log('game_updated')
                const gameIndex = this.games.findIndex(game_ => game_.id === game.id);
                const [ parsedGame ] = this.parseGames([ game ]);
                if (gameIndex > -1) this.games[gameIndex] = parsedGame;
            });

            this.socket.on('games_deleted', (gameIds) => {
                this.refresh();
                if (gameIds.includes(this.playingGame?.id)) this.handleUpdateView('select-game');
            });

            this.socket.on('users_updated', (users = []) => {
                const updatedUsers = this.users.map(user => {
                    const matchingUser = users.find(matchingUser => matchingUser.id === user.id);
                    if(matchingUser) return matchingUser
                    return user
                });
                const parsedUsers = this.parseUsers(updatedUsers);
                this.users = parsedUsers;

                if(!this.user) return
                const you = users.find(user => user.id === this.user.id);
                if(you) this.updateYou(you);
            })

            this.socket.on('user_profile_updated', ({ id, name }) => {
                this.users = this.users.map(user => {
                    if(user.id === id) user.name = name;
                    return user
                });

                this.games = this.games.map(game => {
                    game.users = game.users.map(user => {
                        if(user.id === id) user.name = name;
                        return user
                    });
                    return game;
                });

                if (!this.playingGame) return;
                this.playingGame.users = this.playingGame.users.map(user => {
                    if(user.id === id) user.name = name;
                    return user
                });
            });

            this.socket.on('user_avatar_updated', async ({ id, base64avatar, avatarUploads }) => {
                this.users = this.users.map(user => {
                    if(user.id === id) {
                        user.base64avatar = base64avatar;
                        user.avatarUploads = avatarUploads;
                        user.hasAvatar = true;
                    }
                    return user
                });
                await this.handleClearAvatarCache(id);

                const { avatar } = await this.getBase64Avatar(id);
                if (!avatar) return;

                this.handleUpdateAvatarUploads(id, avatarUploads);

                this.handleUserAvatarInitialized(id, avatar)
            });
        },
        handleCheckAvatarUploads(id, avatarUploads) {
            let localAvatarUploads = localStorage.getItem('userAvatarUploads');
            if (localAvatarUploads) localAvatarUploads = JSON.parse(localAvatarUploads);
            else localAvatarUploads = {};

            return localAvatarUploads[id] == avatarUploads;
        },
        async getUsers() {
            let users = [];
            try {
                users = await httpRequest('GET', '/users_minimal') || [];
            } catch(error) {}
            const parsedUsers = this.parseUsers(users);

            return parsedUsers
        },
        parseUsers(users) {
            const parsed = users.map(user => {
                const existingUser = this.users.find(user_ => user_.id === user.id);
                if (existingUser?.base64avatar && !user.base64avatar) {
                    user.base64avatar = existingUser.base64avatar;
                    user.hasAvatar = existingUser.hasAvatar;
                    user.avatarUploads = existingUser.avatarUploads;
                }
                
                user.exists = true;
                user.keyId = uuidv4();
                return user
            });
            return parsed
        },
        async getGames() {
            let games = [];
            try {
                games = await httpRequest('GET', '/games');
            } catch(error) {}

            return this.parseGames(games);
        },
        parseGames(games = []) {
            return games.map(game => {
                game.users = game.users.map(user => {
                    const existingUser = this.users.find(user_ => user_.id === user.id);
                    if (existingUser?.base64avatar && !user.base64avatar) {
                        user.base64avatar = existingUser.base64avatar;
                        user.hasAvatar = existingUser.hasAvatar;
                        user.avatarUploads = existingUser.avatarUploads;
                    }
                    return user;
                });
                return game;
            });
        },
        updateYou(user) {
            localStorage.setItem('you', JSON.stringify(user));
            this.user = user;
            this.$root.$emit('requestNotificationPermission');
        },
        setUser() {
            const user = localStorage.getItem('you');
            if (!user) return

            const parsedUser = JSON.parse(user);
            const index = this.users.findIndex(user_ => user_.id === parsedUser.id);
            if(index !== -1) {
                localStorage.setItem('you', JSON.stringify(this.users[index]));
                this.user = this.users[index];
            }
        },
        getYou() {
            const user = localStorage.getItem('you');
            if (!user) return null;
            return JSON.parse(user);
        },
        async handleUpdateView(view) {
            this.view = view;

            if (view !== 'play-game') this.socket.emit('leaveGame');

            if (view === 'select-game') this.$router.push('/').catch(error => {});
            else if (view === 'create-game') this.$router.push('/create').catch(error => {});
            else if (view === 'settings') this.$router.push('/settings').catch(error => {});
        },
        updateView() {
            console.log(this.$route.fullPath)
            if(this.$route.fullPath === '/settings') this.view = 'settings'
            else if(this.$route.fullPath === '/' && this.games.length) this.view = 'select-game'
            else if(this.$route.fullPath === '/' || !this.games.length) this.view = 'create-game'
        },
        handleGameCreated(game) {
            this.games.push(game);
            this.handlePlayingGame(game);
            this.$router.push(`/${game.id}`).catch(error => {});
        },
        async handleWindowFocus() {
            await this.refresh();

            if(this.playingGame) this.playingGame = this.games.find(game => game.id === this.playingGame.id);
        },
        handleWindowUnfocus() {
            if (!this.socket || !this.user) return;

            this.socket.emit('unfocus', this.user.id);
        },
        async handleGoBack() {
            this.$router.push('/').catch(error => {});
            this.showSideMenu = false;
        },
        handleUserAvatarInitialized(id, base64avatar) {
            this.users = this.users.map(user => {
                if(user.id === id) {
                    user.base64avatar = base64avatar;
                    user.hasAvatar = true;
                }
                return user
            });

            let you = localStorage.getItem('you');
            if (you && JSON.parse(you).id === id) this.setUser();

            this.games = this.games.map(game => {
                game.users = game.users.map(user => {
                    if(user.id === id) {
                        user.base64avatar = base64avatar;
                        user.hasAvatar = true;
                    }
                    return user
                });
                return game
            });

            if (!this.playingGame) return;
            this.playingGame.users = this.playingGame.users.map(user => {
                if(user.id === id) {
                    user.base64avatar = base64avatar;
                    user.hasAvatar = true;
                }
                return user
            });
        },
        handlePlayingGame(game) {
            const gameId = this.$route.params.id;
            if (!gameId && !game) return this.playingGame = null;

            if (!game) game = this.games.find(game => game.id === gameId);
            if (game) {
                this.playingGame = game;
                this.view = 'play-game';
            }
            else this.playingGame = null;
        },
        async handleInitializeUserAvatars() {
            for(let i = 0; i < this.users.length; i++) {
                const user = this.users[i];
                if (!user.hasAvatar) continue;

                const avatarIsCached = this.handleCheckAvatarUploads(user.id, user.avatarUploads);
                if (!avatarIsCached) await this.handleClearAvatarCache(user.id);

                const { avatar, avatarUploads } = await this.getBase64Avatar(user.id);
                if (!avatar) continue;

                if (!avatarIsCached) this.handleUpdateAvatarUploads(user.id, avatarUploads);

                this.handleUserAvatarInitialized(user.id, avatar);
            }

            this.setUser();
            
            return
        },
        async getBase64Avatar(userId) {
            const response = await httpRequest('GET', `/users/${userId}/avatar`);
            return {
                avatar: response.avatar,
                avatarUploads: response.avatarUploads
            };
        },
        async refresh() {
            this.games = await this.getGames();
            this.users = await this.getUsers();

            if (this.games.length === 0 && this.view === 'select-game') this.handleUpdateView('create-game');

            this.handleInitializeUserAvatars();
            return
        },
        async handleClearAvatarCache(id) {
            const cache = await caches.open('profile-images');
            await cache.delete(`${process.env.VUE_APP_API_URL}/users/${id}/avatar`);
            return;
        },
        handleUpdateAvatarUploads(userId, avatarUploads) {
            // update localStorage with new avatarUploads
            let localAvatarUploads = localStorage.getItem('userAvatarUploads');
            if (localAvatarUploads) localAvatarUploads = JSON.parse(localAvatarUploads);
            else localAvatarUploads = {};

            localAvatarUploads[userId] = avatarUploads;
            localStorage.setItem('userAvatarUploads', JSON.stringify(localAvatarUploads));
        },
        setHistoryState() {
            // console.log(window.history)
            // window.history.pushState({}, null);
            window.history.replaceState({}, null);
        },
        handleCloseSideMenu() {
            this.$refs.sideMenu.close();
        },
        async handleDeleteGame() {
            if(!this.$route.params.id) return;

            try {
                await httpRequest('DELETE', '/games', { gameIds: [this.$route.params.id] })

                this.handleUpdateView('select-game')
                this.handleCloseSideMenu();
            } catch(error) {
                this.handleCloseSideMenu();
            }
        },
        handlePopState(event) {
            if ((this.view === 'create-game' && this.games.length === 0) || this.view === 'select-game') return;
            window.history.pushState({}, '');
        },
    },
    computed: {
        showBackArrow: function() {
            return (this.view !== 'select-game' && this.games.length > 0) || this.view === 'settings'
        },
        usersInMenu: function() {
            if(!this.playingGame) return [ this.user ]
            else return this.users.filter(user => this.playingGame.userIds.includes(user.id))
        },
        showGameComponent() {
            return ['play-game','select-game','create-game'].includes(this.view) && !this.loading && !this.requestNotificationPermission && !this.unableToGrantPermission
        }
    },
    async created() {
        this.loading = true;
        this.createConnection();
        
        this.users = await this.getUsers();
        this.games = await this.getGames();
        this.updateView();

        if(this.games.length === 0 && ['select-game','play-game'].includes(this.view)) {
            this.$router.push('/create').catch(error => {});
        }
        else this.handlePlayingGame();

        this.handleInitializeUserAvatars();

        this.loading = false;

        this.$root.$on('windowfocus', this.handleWindowFocus);
        this.$root.$on('windowunfocus', this.handleWindowUnfocus);
        this.$root.$on('popstate', this.handlePopState);
    },
    beforeDestroy() {
        this.$root.$off('windowfocus', this.handleWindowFocus);
        this.$root.$off('windowunfocus', this.handleWindowUnfocus);
        this.$root.$off('popstate', this.handlePopState);
    },
    watch: {
        '$route.fullPath': async function() {
            this.handlePlayingGame();
            this.updateView();

            if(this.$route.fullPath === '/') {
                this.refresh();
            }

            this.setHistoryState();
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/variables.scss';

.wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;

    .game-wrapper {
        flex-grow: 1;
    }
    
    .center {
        display: grid;
        place-items: center;
        flex-grow: 1;

        .loader {
            --size: calc(var(--absolute-width) / 4);

            position: relative;
            width: var(--size);
            height: var(--size);
            border-radius: 50%;
            mix-blend-mode: lighten;
            background: conic-gradient(#ffffff00 0deg var(--start-degrees), grey var(--start-degrees) var(--end-degrees), #ffffff00 var(--end-degrees) 360deg);

            animation: rotate 2s linear infinite 1s, kaas 1.5s ease-out infinite 1s;
            
            &:after {
                content: "";
                position: absolute;
                inset: 4px;
                border-radius: 50%;
                background: black;
                mix-blend-mode: multiply;
            }
        }
    }
}

@keyframes rotate {
    to {
        rotate: 360deg;
    }
}

@property --start-degrees {
	syntax: "<angle>"; /* this can be angle or percentage */
	initial-value: 0deg;
	inherits: false;
}
@property --end-degrees {
	syntax: "<angle>"; /* this can be angle or percentage */
	initial-value: 0deg;
	inherits: false;
}

@keyframes kaas {
    0% {
        --start-degrees: 0deg;
        --end-degrees: 0deg;
    } 60% {
        --start-degrees: 120deg;
        --end-degrees: 360deg;
    } 100% {
        --start-degrees: 360deg;
        --end-degrees: 360deg;
    }
}

</style>